import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'

interface SingleDateCalendarProps {
  isOpen: boolean
  onClose: () => void
  onDateSelect: (date: Date | null) => void
  selectedDate?: Date | null
  inputRef?: React.RefObject<HTMLInputElement>
}

const SingleDateCalendar = ({
  isOpen,
  onClose,
  onDateSelect,
  selectedDate,
  inputRef
}: SingleDateCalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(selectedDate ? new Date(selectedDate) : new Date())
  const calendarRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(new Date(selectedDate))
    }
  }, [selectedDate])

  // Position calendar below input (portaled) so it's always visible and doesn't block scroll
  useEffect(() => {
    if (!isOpen) {
      setPosition(null)
      return
    }
    const measure = () => {
      if (!inputRef?.current) return
      const rect = inputRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const calendarHeight = 320
      const openAbove = spaceBelow < calendarHeight
      setPosition({
        top: openAbove ? rect.top - calendarHeight - 4 : rect.bottom + 4,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 280 - 8))
      })
    }
    const id = requestAnimationFrame(measure)
    return () => cancelAnimationFrame(id)
  }, [isOpen, inputRef])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        inputRef?.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose, inputRef])

  if (!isOpen) return null

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
  }

  const getPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  }

  const getNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
  }

  const handleDateClick = (date: Date) => {
    onDateSelect(date)
    onClose()
  }

  const isDateSelected = (date: Date) => {
    if (!selectedDate) return false
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    )
  }

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth)
    const firstDay = getFirstDayOfMonth(currentMonth)
    const days: (Date | null)[] = []

    // Add previous month's trailing days
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 0)
    const prevMonthDays = prevMonth.getDate()
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, prevMonthDays - i))
    }

    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i))
    }

    // Add next month's leading days to fill the grid (6 rows * 7 days = 42)
    const remainingDays = 42 - days.length
    for (let i = 1; i <= remainingDays; i++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, i))
    }

    return days.map((date, index) => {
      if (!date) return null

      const isCurrentMonth = date.getMonth() === currentMonth.getMonth()
      const isSelected = isDateSelected(date)

      return (
        <button
          key={index}
          onClick={() => handleDateClick(date)}
          className="w-8 h-8 flex items-center justify-center transition-colors"
          style={{
            backgroundColor: isSelected ? '#212126' : 'transparent',
            color: isSelected ? '#FFFFFF' : (isCurrentMonth ? '#212126' : '#9CA3AF'),
            borderRadius: isSelected ? '6px' : '0px',
            fontFamily: 'Lexend Deca',
            fontWeight: isCurrentMonth ? 600 : 400,
            fontSize: '14px',
            lineHeight: '20px',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            if (!isSelected) {
              e.currentTarget.style.backgroundColor = '#F3F4F6'
            }
          }}
          onMouseLeave={(e) => {
            if (!isSelected) {
              e.currentTarget.style.backgroundColor = 'transparent'
            }
          }}
        >
          {date.getDate()}
        </button>
      )
    })
  }

  const calendarContent = (
    <div
      ref={calendarRef}
      className="bg-white rounded-lg shadow-lg flex flex-col"
      style={{
        position: 'fixed',
        width: '280px',
        border: '1px solid #E9E9EA',
        borderRadius: '8px',
        padding: '16px',
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        zIndex: 9999
      }}
      onClick={(e) => e.stopPropagation()}
    >
        {/* Header */}
        <div className="flex items-center justify-between" style={{ marginBottom: '16px' }}>
          <button
            onClick={getPreviousMonth}
            className="w-8 h-8 flex items-center justify-center rounded transition-colors hover:opacity-80"
            style={{
              backgroundColor: '#F3F4F6',
              borderRadius: '6px'
            }}
          >
            <FiChevronLeft 
              style={{
                width: '16px',
                height: '16px',
                color: '#212126'
              }} 
            />
          </button>
          
          <h3
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '24px',
              color: '#212126'
            }}
          >
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          
          <button
            onClick={getNextMonth}
            className="w-8 h-8 flex items-center justify-center rounded transition-colors hover:opacity-80"
            style={{
              backgroundColor: '#F3F4F6',
              borderRadius: '6px'
            }}
          >
            <FiChevronRight 
              style={{
                width: '16px',
                height: '16px',
                color: '#212126'
              }} 
            />
          </button>
        </div>

        {/* Days of Week */}
        <div className="grid grid-cols-7 gap-1" style={{ marginBottom: '8px' }}>
          {daysOfWeek.map((day) => (
            <div
              key={day}
              className="w-8 h-8 flex items-center justify-center"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontSize: '12px',
                lineHeight: '16px',
                color: '#69697A'
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {renderCalendar()}
        </div>
      </div>
  )

  return position ? createPortal(calendarContent, document.body) : null
}

export default SingleDateCalendar
